import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AppMethodDescriptor } from "../../api";
import { Badge, Button, Dialog, Input, cn } from "../../ui";

/**
 * The endpoint allowlist picker.
 *
 * A backend exposes several hundred app-callable endpoints, so a flat
 * multi-select is unusable — this groups them by controller, filters across
 * code, path and summary, and offers per-group select/clear. Each row shows
 * the HTTP verb and path, because a code like `GET_PRODUCT_LAZY_TYPE` alone
 * doesn't tell an admin what they are granting.
 */
export function MethodPickerDialog({
  open,
  onClose,
  methods,
  loading,
  initial,
  onSave,
  username,
}: {
  open: boolean;
  onClose: () => void;
  methods: AppMethodDescriptor[];
  loading: boolean;
  initial: string[];
  onSave: (codes: string[]) => Promise<void> | void;
  username: string;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial));
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle
      ? methods.filter((method) =>
          `${method.code} ${method.path} ${method.summary} ${method.group}`
            .toLowerCase()
            .includes(needle),
        )
      : methods;

    const byGroup = new Map<string, AppMethodDescriptor[]>();
    for (const method of matching) {
      const list = byGroup.get(method.group) ?? [];
      list.push(method);
      byGroup.set(method.group, list);
    }
    return [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [methods, query]);

  const toggle = (code: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const setGroup = (group: AppMethodDescriptor[], on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const method of group) {
        if (on) next.add(method.code);
        else next.delete(method.code);
      }
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("app-account.methods.title", "Allowed endpoints: {{username}}", { username })}
      description={t(
        "app-account.methods.description",
        "This account can call only what is ticked here. Everything else answers 403.",
      )}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave([...selected]);
              } finally {
                setSaving(false);
              }
            }}
          >
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("grid.search", "Search")}
            className="pl-8"
          />
        </div>
        <Badge variant="accent" size="md">
          {t("badge.selected", "Selected: {{count}}", { count: selected.size })}
        </Badge>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : groups.length === 0 ? (
        <p className="py-8 text-center text-[13px] text-ink-muted">
          {t("table.empty", "Nothing to show")}
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-4">
          {groups.map(([group, items]) => {
            const allOn = items.every((method) => selected.has(method.code));
            return (
              <section key={group}>
                <header className="flex items-center gap-2 border-b border-line pb-1">
                  <h3 className="text-[12px] font-semibold uppercase tracking-wider text-ink-faint">
                    {group}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setGroup(items, !allOn)}
                    className="ml-auto text-[12px] text-accent hover:underline"
                  >
                    {allOn
                      ? t("app-account.methods.clear-group", "Clear group")
                      : t("app-account.methods.select-group", "Select group")}
                  </button>
                </header>

                <ul className="mt-1">
                  {items.map((method) => (
                    <li key={method.code}>
                      <label
                        className={cn(
                          "flex cursor-pointer items-baseline gap-2 rounded px-2 py-1.5 hover:bg-surface-sunk",
                          selected.has(method.code) && "bg-accent-soft",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(method.code)}
                          onChange={() => toggle(method.code)}
                          className="size-3.5 accent-[var(--accent)]"
                        />
                        <span className="w-14 shrink-0 font-mono text-[11px] uppercase text-ink-faint">
                          {method.httpMethod}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-mono text-[12px] text-ink">
                            {method.path}
                          </span>
                          {method.summary && (
                            <span className="block truncate text-[12px] text-ink-muted">
                              {method.summary}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
